import React, { useEffect, useState } from 'react';
import axios from 'axios';

/**
 * The Settings time zone suggestion, shown under the Weather Location card.
 *
 * After a Weather Location save with a home city, Settings passes a new `check`
 * ({ city, state }). This looks the city up (GET /api/weather/geocode), then
 * asks the National Weather Service which zone that point is in
 * (GET /api/weather/timezone). When that zone differs from the company zone it
 * shows one line with a button to use it. Nothing is shown when:
 *   - there is no check (no save yet, or "use my current location" was saved)
 *   - the geocode fell back to the state capital, which can be in another zone
 *   - NWS gave no zone, or the same zone as the company
 *
 * Zones are shown by their picker labels from GET /api/company's
 * timezone_options ("Mountain (Denver)"); a zone the list lacks shows its id.
 * The PUT always sends the id.
 *
 * Only the button changes the zone: PUT /api/company { timezone }, then
 * refreshUser so every page re-renders in it, then onApplied(zone). Dismiss
 * hides the line until the next weather save, which passes a new check.
 */
export default function TimezoneSuggestion({ check, companyZone, zoneOptions, refreshUser, onApplied }) {
  const [nwsZone, setNwsZone] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [applied, setApplied] = useState('');

  useEffect(() => {
    setNwsZone(null);
    setError('');
    setApplied('');
    if (!check || !check.city || !check.state) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const api = process.env.REACT_APP_API_URL;
        const geo = await axios.get(`${api}/api/weather/geocode`, { params: { city: check.city, state: check.state } });
        // Only a city found in the table; an answer without the flag counts as a fallback.
        if (cancelled || !geo.data || geo.data.fallback !== false) return;
        const tz = await axios.get(`${api}/api/weather/timezone`, { params: { lat: geo.data.lat, lon: geo.data.lon } });
        if (!cancelled) setNwsZone((tz.data && tz.data.timeZone) || null);
      } catch (err) {
        // A suggestion only: a failed lookup shows nothing.
      }
    })();
    return () => { cancelled = true; };
  }, [check]);

  const labelFor = (zone) => {
    const found = (Array.isArray(zoneOptions) ? zoneOptions : []).find((o) => o && o.value === zone);
    return (found && found.label) || zone;
  };

  const apply = async () => {
    const zone = nwsZone;
    setSaving(true);
    setError('');
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/company`, { timezone: zone });
      if (onApplied) onApplied(zone);
      // The zone is saved; a failed refresh only delays the re-render.
      if (refreshUser) await Promise.resolve(refreshUser()).catch(() => {});
      setNwsZone(null);
      setApplied(zone);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not change the time zone');
    } finally {
      setSaving(false);
    }
  };

  if (applied) {
    return (
      <div className="alert alert-success" data-testid="timezone-suggestion-applied" style={{ marginTop: '12px' }}>
        Company time zone changed to {labelFor(applied)}.
      </div>
    );
  }

  if (!nwsZone || !companyZone || nwsZone === companyZone) return null;

  return (
    <div className="alert alert-warning" data-testid="timezone-suggestion" role="status" style={{ marginTop: '12px' }}>
      <span>Your weather location is in {labelFor(nwsZone)}; your company time zone is {labelFor(companyZone)}.</span>
      {error && <div style={{ marginTop: '6px' }}>{error}</div>}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
        <button type="button" className="btn btn-primary btn-sm" onClick={apply} disabled={saving}>
          {saving ? 'Saving...' : `Use ${labelFor(nwsZone)}`}
        </button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setNwsZone(null)} disabled={saving}>
          Dismiss
        </button>
      </div>
    </div>
  );
}
